import type { JSX, ReactNode } from 'react'
import { Button } from './Button'

/**
 * One setting as a read-only row that edits in place.
 *
 * Settings consolidation (2026-09-15). The working pages used to hold forms
 * that never closed — there was no read-only state to return to, so a saved
 * page looked unsaved. Here *Edit* swaps the row for its form, and the form's
 * own success handler calls the panel's `close` so Save always lands back on
 * the row. A failed save leaves the form open: the form renders its
 * `InlineError` and the row never sees it.
 *
 * The row is deliberately dumb about *what* it edits. It owns the two states
 * and the stamp; the form owns the fields, the mutation and the failure.
 */
export interface SettingRowProps {
  label: string
  /** The read-only value. `null` renders the `empty` copy. */
  summary: string | null
  empty?: string
  /** When the value was last saved from this screen, as an ISO timestamp. */
  savedAt?: string | null
  editable: boolean
  editing: boolean
  onEdit: () => void
  onCancel: () => void
  /** The form, rendered while `editing`. */
  children: ReactNode
}

export function SettingRow({
  label,
  summary,
  empty = 'Not set',
  savedAt = null,
  editable,
  editing,
  onEdit,
  onCancel,
  children,
}: SettingRowProps): JSX.Element {
  if (editing) {
    return (
      <div className="ar-card app-setting-row app-setting-row--editing">
        <div className="ar-card__header">
          <h3 className="ar-h3">{label}</h3>
          <span className="ar-card__spacer" />
          <Button tone="quiet" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <div className="ar-card__body">{children}</div>
      </div>
    )
  }
  return (
    <div className="ar-card app-setting-row">
      <div className="ar-card__body ar-row">
        <div style={{ flex: 1 }}>
          <strong>{label}</strong>
          <div className={summary === null ? 'ar-subtle' : undefined}>{summary ?? empty}</div>
          {savedAt !== null ? (
            <span className="ar-badge ar-badge--success" role="status">
              {stamp(savedAt)}
            </span>
          ) : null}
        </div>
        {editable ? (
          <Button size="sm" onClick={onEdit} aria-label={`Edit ${label}`}>
            Edit
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** "Saved just now" for the save that just happened; the IST time otherwise. */
function stamp(savedAt: string): string {
  const at = new Date(savedAt)
  if (Date.now() - at.getTime() < 60_000) return 'Saved just now'
  const time = at.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `Saved at ${time}`
}
